/// <amd-module name="Traceability/RunView/Common/RunViewCommonComponent" />
import React = require("react");
import * as BuildContracts from "Build/Client/Contracts/Build";
import { ISimpleBreadCrumbItem, RunViewCommonBreadCrumbService } from "Traceability/RunView/Common/RunViewCommonBreadCrumbService";
// Make sure ITraceabilityLinkingService is registered for FPS
import * as TraceabilityLinkingService from "Traceability/Common/Linking";
import { IRunViewCommonData, IRunViewCommonService, ITraceabilityLinkingService } from "Traceability/RunView/Common/RunViewCommonService.types";
import { localeFormat } from "VSS/Core/Util/String";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Header, TitleSize } from "VSSUI/Header";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssHistoryService } from "VSS/Platform/History";
import {ImageDetails} from "Traceability/Common/ImageDetails";

const imageUriRouteParameter = "imageUri";
const currentRunIdRouteParameter = "currentRunId";

export abstract class RunViewCommonComponent<T> extends VssComponent {
    protected abstract pageTitleText: string;
    protected abstract pageHeaderText: string;
    protected abstract service: IRunViewCommonService<T>;
    protected abstract getCards(pageData: IRunViewCommonData<T>): JSX.Element[];

    public constructor(props: {}, context: IVssComponentContext) {
        super(props, context);
        this._linkService = this.context.pageContext.getService<ITraceabilityLinkingService>("ITraceabilityLinkingService");
    }

    public render(): JSX.Element {
        this._runUrl = this._linkService.getRunUrl(this.context.pageContext, this.service.pageData.currentRunId, this.service.projectName);
        this._imageUri = this._getRouteValue(imageUriRouteParameter) || "";
        if (!this._imageUri) {
            const cards = this.getCards(this.service.pageData);
            return (
                <Surface background={SurfaceBackground.neutral}>
                    <Page className="traceability-page">
                        {this._getHeader()}
                        <div className="page-content page-content-top">{cards}</div>
                    </Page>
                </Surface>
            );
        }
        else {
            this._imageAlias = this._getImageArtifactAlias() || "";
            const onBackButtonClick = () => {
                const currentRunId = Number.parseInt(this._getRouteValue(currentRunIdRouteParameter));
                const runViewTraceabilityUrl = this._linkService.getRunViewTraceabilityUrl(this.context.pageContext, this.service.hubRouteId, currentRunId, this.service.projectName);
                onClickFPS(this.context.pageContext, runViewTraceabilityUrl, true);
            };
            return (
                <ImageDetails imageUri={this._imageUri} onBackButtonClick={onBackButtonClick}/>
            );
        }
    }

    public componentDidMount() {
        this._setBreadcrumbs();
    }

    private _setBreadcrumbs = (): void => {
        this.service.getBuild(this.service.projectId, this.service.pageData.currentRunId).then((runData: BuildContracts.Build) => {
            let items: ISimpleBreadCrumbItem[] = [];
            items.push({
                key: `definitionId_${runData.definition.id}`,
                displayText: runData.definition.name,
                url: this._linkService.getPipelineDefinitionUrl(this.context.pageContext, runData.definition.id, this.service.projectName)
            });
            items.push({
                key: `runId_${runData.id}`,
                displayText: runData.buildNumber,
                url: this._linkService.getRunUrl(this.context.pageContext, runData.id, this.service.projectName)
            });
            items.push({
                key: `itemsRunId_${runData.id}`,
                displayText: this.pageHeaderText,
                url: this._linkService.getRunViewTraceabilityUrl(this.context.pageContext, this.service.hubRouteId, runData.id, this.service.projectName)
            });
            document.title = localeFormat(this.pageTitleText, runData.buildNumber);

            if (this._imageAlias){
                const params = {"imageUri": this._imageUri};
                items.push({
                    key: `itemsImageDetails_${this._imageAlias}`,
                    displayText: this._imageAlias,
                    url: this._linkService.getRunViewTraceabilityUrl(this.context.pageContext, this.service.hubRouteId, runData.id, this.service.projectName, params)
                });
            }
            this.context.pageContext.getService<RunViewCommonBreadCrumbService>("RunViewCommonBreadCrumbService").setBreadcrumb(...items);
        });
    }

    private _getHeader(): JSX.Element {
        return (
            <Header
                className={"traceability-page-header"}
                title={this.pageHeaderText}
                titleSize={TitleSize.Large}
                backButtonProps={{ onClick: this._onBackButtonClick, href: this._runUrl }}
            />
        );
    }

    private _onBackButtonClick = (event: React.SyntheticEvent<HTMLElement, Event>) => {
        onClickFPS(this.context.pageContext, this._runUrl, true, event);
    };

    private _getRouteValue(param: string): string {
        const pageContext = this.context.pageContext;
        const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
        return historyService.getState()[param];
    }

    private _getImageArtifactAlias(): string {
        const artifactsData = this.service.pageData.artifactsData;
        const currentArtifact = (artifactsData || []).find(artifact => {
            const version = artifact.artifactVersion;
            return (version.properties && version.properties["containerUri"] === this._imageUri);
        });
        return currentArtifact ? currentArtifact.artifactVersion.alias : "";
    }

    private _linkService: ITraceabilityLinkingService;
    private _runUrl: string;
    private _imageUri: string = "";
    private _imageAlias: string = "";
}